import { useCallback, useRef, useEffect } from 'react';
import { useCanvasContext } from '../contexts/CanvasContext';
import { logger } from '../utils/logger';

/**
 * Hook that provides a centralized hangup function for the phone
 * @param {Object} options - Optional configuration
 * @param {string} options.source - Source of the hangup (e.g., 'question_sequence', 'keyboard', 'manual')
 * @param {string} options.reason - Reason for hangup (e.g., 'hangup', 'complete', 'escape_key')
 * @returns {Function} hangup - Function to hang up the phone
 */
export const useHangup = (defaultOptions = {}) => {
  const { setPhoneOffHook, setIsInQuestionMode, phoneboxState } = useCanvasContext();
  
  // Store defaultOptions in a ref to avoid recreating the callback on every render
  const defaultOptionsRef = useRef(defaultOptions);
  useEffect(() => {
    defaultOptionsRef.current = defaultOptions;
  }, [defaultOptions]);

  const hangup = useCallback((options = {}) => {
    const { source = defaultOptionsRef.current.source || 'unknown', reason = defaultOptionsRef.current.reason || 'hangup' } = options;
    
    logger.debug('📞 Hangup called', { source, reason });
    
    // Exit question mode if currently in it
    if (phoneboxState.isInQuestionMode) {
      setIsInQuestionMode(false, { source, reason });
    }
    
    // Hang up the phone
    setPhoneOffHook(false, { source, reason });
  }, [setPhoneOffHook, setIsInQuestionMode, phoneboxState.isInQuestionMode]);

  return hangup;
};

export default useHangup;

